import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Sparkles, TrendingUp, BarChart3, Code2, Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
export function Header() {
  const location = useLocation();
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const isActive = (path: string) => location.pathname === path;
  const isServicesActive = location.pathname.startsWith('/services');
  const navLinkClass = (path: string) => `
    px-5 py-2 rounded-lg font-medium transition-colors
    ${isActive(path) ? 'bg-blue-50 text-blue-700 border border-blue-100' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'}
  `;
  const services = [{
    name: 'Creative Experiences',
    path: '/services/creative-experiences',
    icon: Sparkles,
    color: 'text-purple-600',
    bg: 'bg-purple-100'
  }, {
    name: 'SaaS Growth Enablement',
    path: '/services/saas-growth',
    icon: TrendingUp,
    color: 'text-green-600',
    bg: 'bg-green-100'
  }, {
    name: 'Data Analytics',
    path: '/services/data-analytics',
    icon: BarChart3,
    color: 'text-amber-600',
    bg: 'bg-amber-100'
  }, {
    name: 'Product Engineering',
    path: '/services/product-engineering',
    icon: Code2,
    color: 'text-blue-600',
    bg: 'bg-blue-100'
  }];
  return <header className="sticky top-0 z-50 w-full bg-white/80 backdrop-blur-md border-b border-slate-200" data-id="element-147">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-148">
        <div className="relative flex items-center h-20" data-id="element-149">
          {/* Logo - absolutely positioned left */}
          <Link to="/" className="absolute left-4 sm:left-6 lg:left-8 flex items-center" aria-label="Akios — Think bold. Build brilliant." data-id="element-150">
            <img src="https://cdn.magicpatterns.com/uploads/dPy3VAvV5Y6dyUnP324PQk/Logo_Social.png" alt="Akios" className="h-12 w-auto object-contain" data-id="element-151" />
          </Link>

          {/* Desktop Nav - centered */}
          <nav className="hidden md:flex items-center gap-2 mx-auto" data-id="element-152">
            <Link to="/" className={navLinkClass('/')} data-id="element-153">
              Home
            </Link>

            {/* Services Dropdown */}
            <div className="relative" onMouseEnter={() => setIsServicesOpen(true)} onMouseLeave={() => setIsServicesOpen(false)} data-id="element-154">
              <button className={`flex items-center gap-1 px-5 py-2 rounded-lg font-medium transition-colors ${isServicesOpen || isServicesActive ? 'text-blue-700 bg-blue-50 border border-blue-100' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'}`} onClick={() => setIsServicesOpen(!isServicesOpen)} aria-expanded={isServicesOpen} data-id="element-155">
                Services{' '}
                <ChevronDown className={`w-4 h-4 transition-transform ${isServicesOpen ? 'rotate-180' : ''}`} data-id="element-156" />
              </button>

              <AnimatePresence data-id="element-157">
                {isServicesOpen && <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: 10
              }} transition={{
                duration: 0.2
              }} className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-72 bg-white rounded-xl shadow-xl border border-slate-200 p-2 overflow-hidden" data-id="element-158">
                    <div className="flex flex-col gap-1" data-id="element-159">
                      {services.map(service => <Link key={service.name} to={service.path} className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors group" onClick={() => setIsServicesOpen(false)} data-id="element-160">
                          <div className={`p-2 rounded-md ${service.bg} ${service.color} group-hover:scale-110 transition-transform`} data-id="element-161">
                            <service.icon className="w-4 h-4" data-id="element-162" />
                          </div>
                          <span className="text-sm font-semibold text-slate-700 group-hover:text-slate-900" data-id="element-163">
                            {service.name}
                          </span>
                        </Link>)}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            <Link to="/about" className={navLinkClass('/about')} data-id="element-164">
              About
            </Link>
            <Link to="/contact" className={navLinkClass('/contact')} data-id="element-165">
              Contact
            </Link>
          </nav>

          {/* Mobile Menu Button - absolutely positioned right */}
          <div className="md:hidden absolute right-4 sm:right-6 lg:right-8 flex items-center" data-id="element-166">
            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg" aria-label="Toggle menu" data-id="element-167">
              {isMobileMenuOpen ? <X className="w-6 h-6" data-id="element-168" /> : <Menu className="w-6 h-6" data-id="element-169" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Nav */}
      <AnimatePresence data-id="element-170">
        {isMobileMenuOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} className="md:hidden bg-white border-b border-slate-200 overflow-hidden" data-id="element-171">
            <div className="px-4 py-4 flex flex-col gap-2" data-id="element-172">
              <Link to="/" onClick={() => setIsMobileMenuOpen(false)} className={navLinkClass('/')} data-id="element-173">
                Home
              </Link>
              <div className="px-4 py-2 font-medium text-slate-400 text-sm uppercase tracking-wider" data-id="element-174">
                Services
              </div>
              {services.map(service => <Link key={service.name} to={service.path} onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 px-6 py-2 text-slate-600 hover:bg-slate-50 rounded-lg" data-id="element-175">
                  <service.icon className={`w-4 h-4 ${service.color}`} data-id="element-176" />
                  {service.name}
                </Link>)}
              <Link to="/about" onClick={() => setIsMobileMenuOpen(false)} className={navLinkClass('/about')} data-id="element-177">
                About
              </Link>
              <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)} className={navLinkClass('/contact')} data-id="element-178">
                Contact
              </Link>
            </div>
          </motion.div>}
      </AnimatePresence>
    </header>;
}